import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * Step gate service — tracks whether required components on the current step
 * have been satisfied (e.g. photo taken, signature confirmed, scope confirmed).
 *
 * Components call `register(key, required)` when they mount and
 * `update(key, satisfied)` whenever their state changes.
 *
 * `NavEventNavigatorComponent` subscribes to `canProceed$` to enable/disable
 * the confirm button.
 *
 * The service is reset on each step advance via `reset()`.
 */
@Injectable({ providedIn: 'root' })
export class NavStepGateService {
    private gates = new Map<string, { required: boolean; satisfied: boolean }>();
    canProceed$ = new BehaviorSubject<boolean>(true);

    /** Register a gate. Call on component init. */
    register(key: string, required: boolean, initialSatisfied = false) {
        this.gates.set(key, { required, satisfied: initialSatisfied });
        this._emit();
    }

    /** Update satisfaction state. Call whenever the component's state changes. */
    update(key: string, satisfied: boolean) {
        const gate = this.gates.get(key);
        if (!gate) return;
        gate.satisfied = satisfied;
        this._emit();
    }

    /** Clear all gates for the next step. */
    reset() {
        this.gates.clear();
        this.canProceed$.next(true);
    }

    private _emit() {
        const blocked = [...this.gates.values()].some(g => g.required && !g.satisfied);
        this.canProceed$.next(!blocked);
    }
}
